/**
 * SEO-optimized alt text generator for product images.
 *
 * Uses rising queries and high-value keywords from keyword research
 * to make images discoverable by AI visual search and LLM crawlers.
 *
 * The alt text follows the pattern:
 *   [Product name] — [contextual keyword phrase] | Pickify AI Gift Finder
 *
 * This is semantic-friendly without being spammy.
 */

/**
 * Generate optimized alt text for a deal/product image.
 *
 * @param {Object} deal - Deal object with title, source, category, etc.
 * @param {number} index - Position in list (for variety)
 * @returns {string} SEO-optimized alt text
 */
export const getDealAltText = (deal) => {
  const source = deal.source || deal.provider || '';
  const category = deal.displayCategory || deal.occasionValue || '';

  // Build contextual phrase based on deal properties
  const contextParts = [];

  if (category) {
    const categoryMap = {
      'tech': 'personalized tech gift idea',
      'fashion': 'fashion gift for him or her',
      'home': 'home and lifestyle gift deal',
      'beauty': 'beauty gift for women',
      'sports': 'sports and fitness gift',
      'kids': 'gift idea for kids',
      'food': 'food and cooking gift',
    };
    contextParts.push(categoryMap[category.toLowerCase()] || `${category} gift deal`);
  }

  if (source) {
    const sourceMap = {
      'amazon': 'Amazon gift deal',
      'aliexpress': 'AliExpress gift guide pick',
      'etsy': 'unique Etsy gift',
      'ebay': 'eBay gift deal',
    };
    contextParts.push(sourceMap[source.toLowerCase()] || `${source} deal`);
  }

  const context = contextParts.length > 0
    ? contextParts[0]
    : 'AI-recommended gift deal';

  // Keep under 125 chars for screen reader best practice
  const alt = `${deal.title} — ${context} | Pickify`;
  return alt.length > 125 ? `${deal.title} — ${context}` : alt;
};

/**
 * Generate optimized alt text for a gift result image.
 *
 * @param {Object} gift - Gift result object
 * @param {Object} quizContext - Quiz answers for contextual keywords
 * @returns {string} SEO-optimized alt text
 */
export const getGiftAltText = (gift, quizContext = {}) => {
  const recipient = quizContext.recipient || '';
  const occasion = quizContext.occasion || '';

  const recipientPhrases = {
    'boyfriend': 'personalized gift idea for men',
    'girlfriend': 'personalized gift idea for women',
    'mother': "Mother's Day gift recommendation",
    'father': "Father's Day gift recommendation",
    'friend': 'unique gift for a friend',
    'child': 'gift idea for kids',
    'colleague': 'professional gift idea',
    'sibling': 'gift for brother or sister',
    'grandmother': 'thoughtful gift for grandma',
    'grandfather': 'gift idea for grandpa',
  };

  const occasionPhrases = {
    'birthday': 'birthday gift',
    'anniversary': 'anniversary present',
    'holiday': 'holiday gift finder pick',
    'thankyou': 'thank-you gift',
    'graduation': 'graduation gift',
    'justbecause': 'just-because surprise gift',
  };

  const context = recipientPhrases[recipient]
    || occasionPhrases[occasion]
    || 'AI-curated gift recommendation';

  const alt = `${gift.title} — ${context} | Pickify AI Gift Finder`;
  return alt.length > 125 ? `${gift.title} — ${context}` : alt;
};
